import { Injectable, signal, WritableSignal } from "@angular/core";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

@Injectable({
  providedIn: "root",
})
export class VideoService {
  private ffmpeg = new FFmpeg();
  public isLoaded = signal(false);

  private async toUint8Array(file: File): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve(new Uint8Array(reader.result as ArrayBuffer));
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  async loadFFmpeg(progressPercent: WritableSignal<number>): Promise<void> {
    if (!this.isLoaded()) {
      const base = window.location.origin + "/assets/ffmpeg";

      await this.ffmpeg.load({
        coreURL: `${base}/module/core/ffmpeg-core.js`,
        wasmURL: URL.createObjectURL(
          await this.fetchWithProgress(
            `${base}/module/core/ffmpeg-core.wasm`,
            progressPercent,
          ),
        ),
        classWorkerURL: `${base}/ffmpeg/worker.js`,
      });
      this.isLoaded.set(true);
    }
  }

  async fetchWithProgress(
    url: string,
    progressPercent: WritableSignal<number>,
  ): Promise<Blob> {
    const res = await fetch(url);
    if (!res.ok || !res.body) throw new Error(`Failed to load ${url}`);
    const contentLength = parseNumber(res.headers.get("Content-Length")) || 1;
    const reader = res.body.getReader();
    let received = 0;
    const chunks: Uint8Array[] = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        received += value.length;
        progressPercent.set(Math.floor((received / contentLength) * 100));
      }
    }

    return new Blob(chunks);
  }

  async compressVideoWithMediaRecorder(
    file: File,
    processPercent: WritableSignal<number>,
  ): Promise<File> {
    return new Promise(async (resolve, reject) => {
      const video = document.createElement("video");
      video.src = URL.createObjectURL(file);
      video.muted = true;
      video.crossOrigin = "anonymous";

      await video.play();

      const fps = 25;
      const bitrate = 4_000_000;

      const targetHeight = 720;
      const targetWidth = Math.round(
        (video.videoWidth / video.videoHeight) * targetHeight,
      );

      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d");

      const videoStream = canvas.captureStream(fps);

      const fullVideoStream =
        (video as any).captureStream?.() || (video as any).mozCaptureStream?.();
      const audioTracks = fullVideoStream?.getAudioTracks() ?? [];

      audioTracks.forEach((track) => videoStream.addTrack(track));

      const recorder = new MediaRecorder(videoStream, {
        mimeType: "video/webm",
        videoBitsPerSecond: bitrate,
      });

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onerror = (e) =>
        reject((e as any).error || new Error("Recorder error"));

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        resolve(new File([blob], "compressed.webm", { type: "video/webm" }));
      };

      recorder.start();

      const interval = setInterval(() => {
        if (video.paused || video.ended) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        processPercent.set(
          Math.round((video.currentTime / video.duration) * 100),
        );
      }, 1000 / fps);

      video.onended = () => {
        clearInterval(interval);
        recorder.stop();
      };
      video.play();
    });
  }

  async compressVideoWithFFmpeg(
    file: File,
    processPercent: WritableSignal<number>,
  ): Promise<File> {
    const startTime = Date.now();
    // this.ffmpeg.on("log", ({ type, message }) =>
    //   console.log(`[${type}] ${message}`),
    // );

    this.ffmpeg.on("progress", (v) => {
      const percent = Math.min(100, Math.round(v.progress * 100));
      processPercent.set(percent);
      const seconds = (Date.now() - startTime) / 1000;
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
    });
    const inputName = file.name;
    const outputName = "output.webm";

    const data = await this.toUint8Array(file);
    await this.ffmpeg.writeFile(inputName, data);
    await this.ffmpeg.exec([
      "-i",
      inputName,
      "-r",
      "25",
      "-vf",
      "scale='if(gt(a,1),720,-2)':'if(gt(a,1),-2,720)'",
      "-c:v",
      "libvpx",
      "-b:v",
      "500k",
      "-deadline",
      "realtime",
      "-cpu-used",
      "8",
      "-c:a",
      "libvorbis",
      "-b:a",
      "64k",
      "-lag-in-frames",
      "0",
      outputName,
    ]);

    const outputData = await this.ffmpeg.readFile(outputName);

    if (typeof outputData === "string") {
      throw new Error(
        "La sortie de ffmpeg est une string, pas une vidéo binaire.",
      );
    }

    return new File([outputData], outputName, { type: "video/webm" });
  }
}
